/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState } from "react";
import {
  useReactTable,
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  OnChangeFn,
} from "@tanstack/react-table";
import { If, Then, Else, When } from "react-if";
import { FlexContainer, Table, Text, theme } from "@nordcloud/gnui";
import { getFirstItem, isEmpty, isNotNil } from "~/tools";
import {
  TableContainer,
  StyledTable,
  StyledRow,
  SVGIconDark,
  SVGIconLight,
  StyledTd,
  StyledThV2,
} from "./styles";
import { ITablePropsV2 } from "./types";

export function ReactTableV2({
  columns,
  data,
  striped,
  hover,
  small,
  mb = "0",
  headerBackground,
  onSort,
  defaultSort,
  sort,
}: ITablePropsV2) {
  const [sorting, setSorting] = useState<SortingState>(
    isNotNil(defaultSort) ? [defaultSort] : []
  );
  const [rowSelection, setRowSelection] = useState({});

  const onSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    const newSort: SortingState =
      typeof updaterOrValue === "function"
        ? updaterOrValue(sorting)
        : updaterOrValue;

    const sortItem = getFirstItem(newSort);
    setSorting(newSort);
    onSort?.({
      desc: sortItem.desc,
      id: sortItem.id,
    });
  };

  const table = useReactTable({
    columns,
    data,
    state: {
      sorting,
      rowSelection,
    },
    enableRowSelection: true,
    enableSortingRemoval: false,
    manualSorting: true,
    enableSorting: sort ?? false,
    onRowSelectionChange: setRowSelection,
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isEmpty(table.getRowModel().rows)) {
    return <Text>No data available.</Text>;
  }
  return (
    <TableContainer>
      <StyledTable
        striped={striped ?? false}
        hoverline={hover}
        small={small}
        mb={mb}
      >
        <Table.thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <StyledThV2
                    key={header.id}
                    isSortable={header.column.getCanSort()}
                    style={{
                      width: (
                        header.column.columnDef?.meta as { width?: string }
                      )?.width,
                      backgroundColor: headerBackground,
                    }}
                    onClick={() => {
                      header.column.getCanSort() &&
                        header.column.toggleSorting(
                          header.column.getIsSorted() !== "desc"
                        );
                    }}
                  >
                    <FlexContainer justifyContent="space-between">
                      <span>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </span>
                      <When condition={header.column.getCanSort()}>
                        <If condition={header.column.getIsSorted()}>
                          <Then>
                            <SVGIconDark
                              size="sm"
                              css={{ paddingBottom: theme.spacing.spacing01 }}
                              name={
                                header.column.getIsSorted() === "desc"
                                  ? "sortDescending"
                                  : "sortAscending"
                              }
                            />
                          </Then>
                          <Else>
                            <SVGIconLight
                              name="sortInactive"
                              size="sm"
                              css={{ paddingBottom: theme.spacing.spacing01 }}
                            />
                          </Else>
                        </If>
                      </When>
                    </FlexContainer>
                  </StyledThV2>
                );
              })}
            </Table.tr>
          ))}
        </Table.thead>
        <Table.tbody>
          {table.getRowModel().rows.map((row) => {
            return (
              <StyledRow key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  return (
                    <StyledTd key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </StyledTd>
                  );
                })}
              </StyledRow>
            );
          })}
        </Table.tbody>
      </StyledTable>
    </TableContainer>
  );
}
